import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { AgentThreadDrawer } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-drawer';
import { AgentThreadRowActions } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-row-actions';
import { AgentThreadSidebar } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-sidebar';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import {
  Button,
  EmptyState,
  IconButton,
  Icons,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { isSuperintelligenceEnabled } from '@/experiences/sales-portal/domain/superintelligence/constants';
import { isWeb } from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalTabParams } from '@/navigators/types';
import { StackScreenProps } from '@react-navigation/stack';
import { SuperintelligenceRuntimeProvider } from '@/experiences/sales-portal/domain/superintelligence/containers/superintelligence-runtime-provider';
import { SuperintelligenceThread } from '@/experiences/sales-portal/domain/superintelligence/components/superintelligence-thread';
import { useAui } from '@assistant-ui/store';
import { useOpenThread } from '@/experiences/sales-portal/domain/superintelligence/blocs/hooks/use-open-thread';
import { View } from 'react-native';

const NEW_CONVERSATION_ICON_SIZE = 16;

type Props = StackScreenProps<
  SalesPortalTabParams,
  typeof SALES_PORTAL_ROUTES.Superintelligence
>;

const SuperintelligencePage: React.FC<{ title: string }> = ({ title }) => {
  const [styles] = useStyles();
  const theme = useTheme();
  const aui = useAui();
  const { layoutSize } = useLayoutContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { archived, persisted, threadId, title: threadTitle } = useOpenThread();
  // Same breakpoint as the shell's sidebar.
  const compact =
    layoutSize === LayoutSize.SMALL || layoutSize === LayoutSize.SMALL_TABLET;

  return (
    <AppPageLayout
      actions={
        compact ? (
          <XStack
            gap={theme.sk.spacing.spaceXsmall}
            paddingVertical={theme.sk.spacing.spaceSmall}
          >
            <IconButton
              aria-label={i18n.t(
                'sales_portal.superintelligence.conversations',
              )}
              icon={<Icons.History color={theme.sk.color.icon.primary} />}
              onPress={() => setDrawerOpen(true)}
              shape="rounded"
              size="$sm"
              variant="tertiary"
            />
            <IconButton
              aria-label={i18n.t(
                'sales_portal.superintelligence.new_conversation',
              )}
              icon={<Icons.SquarePen color={theme.sk.color.icon.primary} />}
              onPress={() => aui.threads.switchToNewThread()}
              shape="rounded"
              size="$sm"
              variant="tertiary"
            />
          </XStack>
        ) : (
          <XStack alignItems="center" gap={theme.sk.spacing.spaceXsmall}>
            {persisted ? (
              <AgentThreadRowActions
                archived={archived}
                threadId={threadId}
                title={threadTitle ?? null}
              />
            ) : null}
            <Button
              icon={
                <Icons.SquarePen
                  color={theme.sk.color.icon.primary}
                  size={NEW_CONVERSATION_ICON_SIZE}
                />
              }
              onPress={() => aui.threads.switchToNewThread()}
              size="$sm"
              variant="secondary"
            >
              {i18n.t('sales_portal.superintelligence.new_conversation')}
            </Button>
          </XStack>
        )
      }
      scroll={false}
      title={
        compact
          ? title
          : (threadTitle ??
            i18n.t('sales_portal.superintelligence.new_conversation'))
      }
    >
      <View style={styles.row}>
        {compact ? null : <AgentThreadSidebar />}
        <View style={styles.thread}>
          <SuperintelligenceThread />
        </View>
      </View>
      {compact ? (
        <AgentThreadDrawer onOpenChange={setDrawerOpen} open={drawerOpen} />
      ) : null}
    </AppPageLayout>
  );
};

export const SuperintelligenceScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const [mounted, setMounted] = useState(!isWeb);
  useEffect(() => {
    setMounted(true);
  }, []);
  const title = i18n.t('sales_portal.superintelligence.title');
  const handleThreadIdChange = useCallback(
    (threadId: string | undefined) => navigation.setParams({ threadId }),
    [navigation],
  );

  if (!isSuperintelligenceEnabled()) {
    return (
      <SalesHubShellTemplate>
        <AppPageLayout scroll={false} title={title}>
          <EmptyState
            title={i18n.t('sales_portal.superintelligence.unavailable')}
          />
        </AppPageLayout>
      </SalesHubShellTemplate>
    );
  }

  return (
    <SalesHubShellTemplate>
      {mounted ? (
        <SuperintelligenceRuntimeProvider
          initialThreadId={route.params?.threadId}
          onThreadIdChange={handleThreadIdChange}
        >
          <SuperintelligencePage title={title} />
        </SuperintelligenceRuntimeProvider>
      ) : (
        <AppPageLayout scroll={false} title={title} />
      )}
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  row: {
    flex: 1,
    flexDirection: 'row',
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  thread: {
    flex: 1,
  },
}));
